import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { streamersData, getStreamerDisplayName } from "../App";
import "../VideoPage.css";
import { parseEmotes } from "emotettv";
import twitchEmotes from "../data/twitch_emotes.json";

// Map of streamer IDs to their Twitch channel IDs
const streamerChannelIds = {
  arrowwoods: "116289532",
  archiedos: "547064977",
  dobrorulitura: "1121206444",
};

// Map of badge IDs to their image URLs
const badgeMap = {
  "share-the-love":
    "https://static-cdn.jtvnw.net/badges/v1/2de71f4f-b152-4308-a426-127a4cf8003a/1",
  "marathon-reveal-runner":
    "https://static-cdn.jtvnw.net/badges/v1/ae1c6c62-c057-4fad-a1d4-663bf988701f/1",
  "purple-pixel-heart---together-for-good-24":
    "https://static-cdn.jtvnw.net/badges/v1/1afb4b76-8c34-4b7b-8beb-75f7e5d2a1ab/1",
  "subtember-2024":
    "https://static-cdn.jtvnw.net/badges/v1/4149750c-9582-4515-9e22-da7d5437643b/1",
  "la-velada-iv":
    "https://static-cdn.jtvnw.net/badges/v1/655dac77-0b2f-4b62-8871-6ae21f82b34e/1",
  "twitch-dj":
    "https://static-cdn.jtvnw.net/badges/v1/cf91bbc0-0332-413a-a7f3-e36bac08b624/1",
  "artist-badge":
    "https://static-cdn.jtvnw.net/badges/v1/4300a897-03dc-4e83-8c0e-c332fee7057f/1",
  broadcaster:
    "https://static-cdn.jtvnw.net/badges/v1/5527c58c-fb7d-422d-b71b-f309dcb85cc1/1",
  founder:
    "https://static-cdn.jtvnw.net/badges/v1/511b78a9-ab37-472f-9569-457753bbe7d3/1",
  "glhf-pledge":
    "https://static-cdn.jtvnw.net/badges/v1/3158e758-3cb4-43c5-94b3-7639810451c5/1",
  glitchcon2020:
    "https://static-cdn.jtvnw.net/badges/v1/1d4b03b9-51ea-42c9-8f29-698e3c85be3d/1",
  "gold-pixel-heart":
    "https://static-cdn.jtvnw.net/badges/v1/1687873b-cf38-412c-aad3-f9a4ce17f8b6/1",
  moderator:
    "https://static-cdn.jtvnw.net/badges/v1/3267646d-33f0-4b17-b3df-f923a41db1d0/1",
  partner:
    "https://static-cdn.jtvnw.net/badges/v1/d12a2e27-16f6-41d0-ab77-b780518f00a3/1",
  premium:
    "https://static-cdn.jtvnw.net/badges/v1/bbbe0db0-a598-423e-86d0-f9fb98ca1933/1",
  "rplace-2023":
    "https://static-cdn.jtvnw.net/badges/v1/e33e0c67-c380-4241-828a-099c46e51c66/1",
  subscriber:
    "https://static-cdn.jtvnw.net/badges/v1/5d9f2208-5dd8-11e7-8513-2ff4adfae661/1",
  twitchbot:
    "https://static-cdn.jtvnw.net/badges/v1/df9095f6-a8a0-4cc2-bb33-d908c0adffb8/1",
  vip: "https://static-cdn.jtvnw.net/badges/v1/b817aba4-fad8-49e2-b88a-7cc744dfa6ec/1",
  "twitch-recap-2023":
    "https://static-cdn.jtvnw.net/badges/v1/4d9e9812-ba9b-48a6-8690-13f3f338ee65/1",
  no_audio:
    "https://static-cdn.jtvnw.net/badges/v1/aef2cd08-f29b-45a1-8c12-d44d7fd5e6f0/1",
  no_video:
    "https://static-cdn.jtvnw.net/badges/v1/199a0dba-58f3-494e-a7fc-1fa0a1001fb8/1",
  "gone-bananas":
    "https://static-cdn.jtvnw.net/badges/v1/e2ba99f4-6079-44d1-8c07-4ca6b58de61f/1",
  "twitch-recap-2024":
    "https://static-cdn.jtvnw.net/badges/v1/72f2a6ac-3d9b-4406-b9e9-998b27182f61/1",
  "zevent-2024":
    "https://static-cdn.jtvnw.net/badges/v1/2040d479-b815-4617-8a55-9aed027e30d0/1",
  twitchconEU2022:
    "https://static-cdn.jtvnw.net/badges/v1/e4744003-50b7-4eb8-9b47-a7b1616a30c6/1",
  "superultracombo-2023":
    "https://static-cdn.jtvnw.net/badges/v1/5864739a-5e58-4623-9450-a2c0555ef90b/1",
};

function VideoPage() {
  const { streamer, videoId } = useParams();
  const location = useLocation();
  const fromPage = location.state?.fromPage || 1;
  const [videoData, setVideoData] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [lastVideoTime, setLastVideoTime] = useState(0);
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [chatLoaded, setChatLoaded] = useState(false);
  const [parsedMessages, setParsedMessages] = useState({});

  const chatContainerRef = useRef(null);
  const playerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const loadChatDataRef = useRef(async () => {});

  const maxMessagesNumber = 100;

  // Get the channel ID for the current streamer
  const channelId = streamerChannelIds[streamer] || "116289532";

  // Parser options with dynamic channelId
  const parserOptions = {
    channelId: channelId,
    providers: {
      twitch: true,
      bttv: true,
      ffz: true,
      seventv: true,
    },
  };

  // Format time in HH:MM:SS
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const chatNearBottomPx = 120;

  // Check if chat is scrolled to bottom (larger threshold = more lenient follow)
  const isNearBottom = (element, threshold = chatNearBottomPx) => {
    return (
      element.scrollHeight - element.scrollTop - element.clientHeight <
      threshold
    );
  };

  // Load video data directly from the imported data
  useEffect(() => {
    if (streamersData[streamer]) {
      const videos = streamersData[streamer];
      const video = videos.find((v) => v.twitch_highlight_id === videoId);

      if (video) {
        setVideoData(video);
        setLoading(false);
      } else {
        setError("Video not found");
        setLoading(false);
      }
    } else {
      setError("Streamer not found");
      setLoading(false);
    }
  }, [streamer, videoId]);

  const loadChatData = async () => {
    if (chatLoaded) return;

    try {
      if (videoData && videoData.chat_url) {
        const chatResponse = await fetch(videoData.chat_url);
        if (!chatResponse.ok) {
          throw new Error("Failed to fetch chat data");
        }
        const chatData = await chatResponse.json();

        const sortedMessages = chatData.comments.sort(
          (a, b) => a.content_offset_seconds - b.content_offset_seconds
        );

        setChatMessages(sortedMessages);
        setChatLoaded(true);
      }
    } catch (err) {
      console.error("Failed to load chat data:", err);
    }
  };

  loadChatDataRef.current = loadChatData;

  // Initialize VK Player when video data is loaded
  useEffect(() => {
    if (!videoData) return;

    const initPlayer = () => {
      const iframe = document.getElementById("vk-player");
      if (iframe && window.VK && window.VK.VideoPlayer) {
        playerRef.current = window.VK.VideoPlayer(iframe);

        playerRef.current.on("inited", function () {
          console.log("Player is ready");
        });

        playerRef.current.on("started", function () {
          console.log("Video started playing");
          loadChatDataRef.current();
        });

        playerRef.current.on("resumed", function () {
          console.log("Video resumed playing");
        });

        playerRef.current.on("paused", function () {
          console.log("Video paused");
        });

        playerRef.current.on("timeupdate", function (timeInfo) {
          setCurrentVideoTime(timeInfo.time);
        });
      } else {
        // If VK API is not ready yet, retry after a short delay
        setTimeout(initPlayer, 100);
      }
    };

    // Load VK Player API if not already loaded
    if (!document.getElementById("vk-player-api")) {
      const script = document.createElement("script");
      script.id = "vk-player-api";
      script.src = "https://vk.com/js/api/videoplayer.js";
      script.async = true;
      script.onload = initPlayer;
      document.body.appendChild(script);
    } else {
      initPlayer();
    }

    // Cleanup function
    return () => {
      if (playerRef.current) {
        playerRef.current.off("timeupdate");
        playerRef.current.off("inited");
        playerRef.current.off("started");
        playerRef.current.off("resumed");
        playerRef.current.off("paused");
      }
    };
  }, [videoData]);

  const handleChatScroll = () => {
    if (chatContainerRef.current) {
      shouldAutoScrollRef.current = isNearBottom(chatContainerRef.current);
    }
  };

  // Show messages around a specific timestamp
  const showMessagesAroundTime = (timestamp) => {
    // Find messages that occurred before the timestamp
    const recentMessages = chatMessages
      .filter((msg) => msg.content_offset_seconds <= timestamp)
      .slice(-maxMessagesNumber); // Get last maxMessagesNumber messages

    // Update displayed messages
    setDisplayedMessages(recentMessages);

    // Parse emotes for messages that appear after seeking
    recentMessages.forEach(async (msg) => {
      if (!parsedMessages[msg._id]) {
        try {
          // First parse with emotettv
          const parsedMessage = await parseEmotes(
            msg.message.body,
            {},
            parserOptions
          );

          // Then parse with twitch emotes
          let finalMessage = parsedMessage.toHTML();
          Object.entries(twitchEmotes).forEach(([emoteText, emoteUrl]) => {
            // Escape special regex characters in the emote text
            const escapedEmoteText = emoteText.replace(
              /[.*+?^${}()|[\]\\]/g,
              "\\$&"
            );
            const regex = new RegExp(`\\b${escapedEmoteText}\\b`, "g");
            finalMessage = finalMessage.replace(
              regex,
              `<img src="${emoteUrl}" alt="${emoteText}" class="chat-emote" />`
            );
          });

          setParsedMessages((prev) => ({
            ...prev,
            [msg._id]: finalMessage,
          }));
        } catch (err) {
          console.error("Failed to parse emotes:", err);
          // Fallback to plain text if parsing fails
          setParsedMessages((prev) => ({
            ...prev,
            [msg._id]: msg.message.body,
          }));
        }
      }
    });

    // Always scroll to bottom on seek and re-enable auto-scroll
    shouldAutoScrollRef.current = true;
    if (chatContainerRef.current) {
      setTimeout(() => {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }, 0);
    }
  };

  // Update chat based on video time
  useEffect(() => {
    if (!chatLoaded || chatMessages.length === 0) return;

    // Check if video has been seeked
    const timeDiff = Math.abs(currentVideoTime - lastVideoTime);
    if (timeDiff > 1) {
      // If time difference is more than 1 second, consider it a seek
      console.log("Video seeked, showing messages around", currentVideoTime);
      showMessagesAroundTime(currentVideoTime);
    } else {
      // Show new messages that should appear at the current time
      const newMessages = chatMessages.filter(
        (msg) =>
          msg.content_offset_seconds <= currentVideoTime &&
          msg.content_offset_seconds > lastVideoTime
      );

      if (newMessages.length > 0) {
        setDisplayedMessages((prev) => {
          // Keep only the last maxMessagesNumber messages to prevent too many messages
          const updatedMessages = [...prev, ...newMessages];
          return updatedMessages.slice(
            Math.max(0, updatedMessages.length - maxMessagesNumber)
          );
        });

        // Parse emotes for new messages
        newMessages.forEach(async (msg) => {
          if (!parsedMessages[msg._id]) {
            try {
              // First parse with emotettv
              const parsedMessage = await parseEmotes(
                msg.message.body,
                {},
                parserOptions
              );

              // Then parse with twitch emotes
              let finalMessage = parsedMessage.toHTML();
              Object.entries(twitchEmotes).forEach(([emoteText, emoteUrl]) => {
                // Escape special regex characters in the emote text
                const escapedEmoteText = emoteText.replace(
                  /[.*+?^${}()|[\]\\]/g,
                  "\\$&"
                );
                const regex = new RegExp(`\\b${escapedEmoteText}\\b`, "g");
                finalMessage = finalMessage.replace(
                  regex,
                  `<img src="${emoteUrl}" alt="${emoteText}" class="chat-emote" />`
                );
              });

              setParsedMessages((prev) => ({
                ...prev,
                [msg._id]: finalMessage,
              }));
            } catch (err) {
              console.error("Failed to parse emotes:", err);
              // Fallback to plain text if parsing fails
              setParsedMessages((prev) => ({
                ...prev,
                [msg._id]: msg.message.body,
              }));
            }
          }
        });

        // Log new messages for debugging
        newMessages.forEach((msg) => {
          console.log("Showing message:", {
            id: msg._id,
            offset: msg.content_offset_seconds,
            currentTime: currentVideoTime,
          });
        });

        // Scroll to bottom if auto-scroll is enabled
        if (shouldAutoScrollRef.current && chatContainerRef.current) {
          setTimeout(() => {
            chatContainerRef.current.scrollTop =
              chatContainerRef.current.scrollHeight;
          }, 0);
        }
      }
    }

    setLastVideoTime(currentVideoTime);
    // Intentionally omit parsedMessages, lastVideoTime, showMessagesAroundTime, parserOptions:
    // adding them would re-run on every emote parse or cause duplicate/looping updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chat sync tied to playback time only
  }, [currentVideoTime, chatMessages, chatLoaded]);

  // Update page title based on streamer and video
  useEffect(() => {
    if (streamer && videoData) {
      document.title = `${
        videoData.twitch_highlight_title
      } - ${getStreamerDisplayName(streamer)}`;
    }
  }, [streamer, videoData]);

  if (loading) {
    return <div className="video-page-container">Loading video...</div>;
  }

  if (error) {
    return <div className="video-page-container">Error: {error}</div>;
  }

  return (
    <div className="container">
      <div className="video-container">
        <iframe
          id="vk-player"
          title={videoData.twitch_highlight_title || "Video player"}
          src={videoData.iframe_src}
          width="100%"
          height="100%"
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture;"
          frameBorder="0"
          allowFullScreen
        ></iframe>
      </div>

      <div className="chat-section">
        <div className="chat-header">
          <Link
            to={fromPage > 1 ? `/${streamer}/page/${fromPage}` : `/${streamer}`}
            className="back-button"
          >
            Назад
          </Link>
        </div>
        <div className="chat-container" ref={chatContainerRef} onScroll={handleChatScroll}>
          <div id="chat-messages">
            {displayedMessages.map((msg) => (
              <div key={msg._id} className="chat-message">
                <div className="message-content">
                  {msg.message.user_badges &&
                    msg.message.user_badges.map((badge, index) => {
                      const badgeId = badge._id;
                      const badgeUrl = badgeMap[badgeId];

                      return badgeUrl ? (
                        <img
                          key={index}
                          src={badgeUrl}
                          alt={badgeId}
                          className="chat-badge"
                          style={{ marginRight: "5px" }}
                        />
                      ) : null;
                    })}
                  <span
                    className="username"
                    style={{ color: msg.message.user_color || "#ffffff" }}
                  >
                    {msg.commenter.display_name}
                  </span>
                  :{" "}
                  {parsedMessages[msg._id] ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: parsedMessages[msg._id],
                      }}
                    />
                  ) : (
                    msg.message.body
                  )}
                </div>
                <div className="timestamp">
                  {formatTime(msg.content_offset_seconds)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoPage;
